import styled, { css } from 'styled-components';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Button from './Button';
import useOnClickOutside from '../Dropdown/useOnClickOutside';

interface Action {
    key: string;
    label: ReactNode;
    action: () => void;
    show: boolean;
    dataTestId?: string;
}

interface MultiactionButtonProps {
    children: ReactNode;
    actions: Action[];
    dataTestId?: string;
    noneButton?: boolean;
    large?: boolean;
    primary?: boolean;
    disabled?: boolean;
    dropdownPosition?: Position;
}

export enum Position {
    Above = 'above',
    Below = 'below',
}

const MultiactionButton: React.FC<MultiactionButtonProps> = ({
    children,
    actions,
    dataTestId,
    noneButton,
    large = true,
    primary = true,
    disabled = false,
    dropdownPosition = Position.Below,
}: MultiactionButtonProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const refDropdown = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(true);
    const [visibility, setVisibility] = useState(false);
    const [buttonHeight, setButtonHeight] = useState(0);
    const [position, setPosition] = useState<Position>(dropdownPosition);
    const kebabIconHeightRem = 3;

    useEffect(() => {
        setButtonHeight(refDropdown.current?.clientHeight ?? 0);
        setOpen(false);
        setVisibility(true);
    }, []);

    const handleClickOutside = () => {
        if (open) setOpen(!open);
    };

    useOnClickOutside(ref, handleClickOutside);

    const convertRemToPixels = (rem: number) => {
        return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    };

    const checkIfEnoughSpaceForDropdownBelow = () => {
        return (
            document.documentElement.clientHeight - ref.current!.getBoundingClientRect().y >
            buttonHeight + convertRemToPixels(kebabIconHeightRem)
        );
    };

    const onClick = () => {
        setOpen(!open);

        if (checkIfEnoughSpaceForDropdownBelow()) {
            setPosition(Position.Below);
        } else {
            setPosition(Position.Above);
        }
    };

    const renderOptions = () => {
        const documentIsScrollable = document.body.scrollHeight > window.innerHeight;

        return (
            <Dropdown
                ref={refDropdown}
                $visibility={visibility}
                $display={open}
                $noneButton={noneButton ?? false}
                $position={position}
                $iconHeightRem={kebabIconHeightRem}
                $fixed={!documentIsScrollable}
                $large={large}
                data-testid="multi-action-dropdown"
            >
                {actions.map(x => {
                    return (
                        x.show && (
                            <ActionSelect
                                key={`action-${x.key}`}
                                onClick={() => x.action()}
                                data-testid={x.dataTestId}
                            >
                                {x.label}
                            </ActionSelect>
                        )
                    );
                })}
            </Dropdown>
        );
    };

    return (
        <div ref={ref}>
            {noneButton ? (
                <NoneButton onClick={onClick} data-testid={dataTestId}>
                    {children}
                    {renderOptions()}
                </NoneButton>
            ) : (
                <MultiButton
                    large={large}
                    onClick={() => !disabled && setOpen(!open)}
                    primary={primary}
                    disabled={disabled}
                    data-testid={dataTestId}
                >
                    <Header>{children}</Header>
                    {renderOptions()}
                </MultiButton>
            )}
        </div>
    );
};

export default MultiactionButton;

const NoneButton = styled.div`
    cursor: pointer;
    position: relative;
`;

const MultiButton = styled(Button)`
    position: relative;
    display: inline-block;
`;

const Header = styled.div`
    justify-content: space-between;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 1rem;
`;

const Dropdown = styled.div<{
    $visibility: boolean;
    $display: boolean;
    $noneButton: boolean;
    $position: Position;
    $iconHeightRem: number;
    $fixed: boolean;
    $large: boolean;
}>`
    position: absolute;
    visibility: ${props => (props.$visibility ? 'visible' : 'hidden')};
    display: ${props => (props.$display ? 'block' : 'none')};
    box-shadow: 0 0.8rem 1.6rem 0 ${props => props.theme.colors.shadow};
    background-color: ${props => props.theme.colors.light};
    border: 0.1rem solid ${props => props.theme.colors.border};

    ${props =>
        props.$noneButton
            ? css`
                  width: fit-content;
                  transform: translateX(calc(-100% + 2.5rem));
              `
            : css`
                  max-width: fit-content;
                  min-width: 100%;
                  top: ${props.$large ? '4rem' : '2.7rem'};
                  right: 0;
              `}

    ${props =>
        props.$position === Position.Above &&
        (props.$noneButton
            ? css`
                  transform: translateX(calc(-100% + 2.5rem))
                      translateY(calc(-100% - ${props.$iconHeightRem}rem));
              `
            : css`
                  transform: translateY(calc(-100% - ${props.$iconHeightRem}rem));
              `)}

    ${props =>
        props.$noneButton &&
        props.$fixed &&
        css`
            position: fixed;
        `}

    z-index: 1;
`;

const ActionSelect = styled.div`
    padding: 1.5rem;
    color: ${props => props.theme.colors.text.primary};
    border-bottom: 0.1rem solid ${props => props.theme.colors.border};

    display: flex;
    align-items: center;
    justify-content: center;
    justify-content: left;
    white-space: nowrap;

    gap: 0.5rem;

    &:hover {
        background-color: ${props => props.theme.colors.text.subtle};
    }
`;
